const failedSignInAttempts = new Map();
const failedOTPAttempts = new Map();
const resendOTPAttempts = new Map();
let mockOTPAuthSequence = 0;

/**
 * MOCK AUTHENTICATION FLOWS:
 * 
 * FLOW 1 - OAuth Session with Code (user already has session):
 *    
 *   1. GET http://localhost:3000/txtsso/auth/v1/authorize?client_id=my-test-app&redirect_uri=https://example-app/callback&response_type=code&scope=openid%20profile%20email&state=random-state-123&code_challenge=test-challenge&code_challenge_method=S256&mock_session=1 → Returns location WITH code + user details
 *   2. Navigate to /verifyConsent (hasExistingSession determined by presence of code = true)
 *   3. Shows "Continue As" page (uses user details)
 *   4. POST /verifyConsent → Returns 404 (no consent)
 *   5. Show "Grant Permission" page
 *   6. POST /acceptConsent → Returns 200
 *   7. Redirect to app with code
 * 
 * FLOW 2 - OAuthRegular Login (no existing session):
 *   1. GET http://localhost:3000/txtsso/auth/v1/authorize?client_id=my-test-app&redirect_uri=https://example-app/callback&response_type=code&scope=openid%20profile%20email&state=random-state-123&code_challenge=test-challenge&code_challenge_method=S256→ Returns location WITHOUT code, no user details
 *   2. Navigate to / (sign-in page, hasExistingSession defaults to false)
 *   3. POST /authenticate → Returns MFA choices + userAliasId
 *   4. POST /mfaChoices → Returns OTP callbacks
 *   5. POST /processOTP → Returns tokenId
 *   6. GET /authorize (with userAliasId) → Returns location WITH code, no user details
 *   7. Navigate to /verifyConsent (hasExistingSession still false)
 *   8. Shows "Grant Permission" page (skips "Continue As")
 *   9. POST /verifyConsent → Returns 404 (no consent) + appName
 *   10. POST /acceptConsent → Returns 200
 *   11. Redirect to app with code
 *   
 *   
 * TEST SCENARIO: mock_error=authorize_fail → Authorize endpoint fails with 500
 * 
 * To test skipping consent (both flows):
 *   - Use client_id: 'client-skip-consent' → verifyConsent returns 200
 *
 * GRANT PERMISSION SCOPE TESTING (tests conditional rendering of DL/ID and DOB):
 *   - scope=openid+profile+dlIdInfo+dobInfo → Shows BOTH DL/ID and DOB items
 *   - scope=openid+profile+dlIdInfo → Shows ONLY DL/ID item
 *   - scope=openid+profile+dobInfo → Shows ONLY DOB item
 *   - scope=openid+profile → Shows NEITHER DL/ID nor DOB items (only name, email, phone, address)
 * 
 *  FLOW 3 - SAML flow (with spEntityId):
 *   1. GET /txtsso/saml/v1/auth?spEntityID=demo-saml-client&goto=<saml-continue-url> → capture params, replace the URL with /
 *   2. POST /getCustomerAppInfo with clientId=demo-saml-client → Returns app metadata
 *   3. Navigate to / (sign-in page)
 *   4. SAML success if authenticate has token id → redirects to the goto URL
 *   5. Regular SAML flow after process OTP success → redirects to the goto URL
 */

const getFailedAttemptCount = username => failedSignInAttempts.get(username) || 0;

const incrementFailedAttempts = username => {
  const nextCount = getFailedAttemptCount(username) + 1;
  failedSignInAttempts.set(username, nextCount);
  return nextCount;
};

const clearFailedAttempts = username => {
  failedSignInAttempts.delete(username);
};

const getOTPAttemptCount = authId => failedOTPAttempts.get(authId) || 0;

const incrementOTPAttempts = authId => {
  const nextCount = getOTPAttemptCount(authId) + 1;
  failedOTPAttempts.set(authId, nextCount);
  return nextCount;
};

const clearOTPAttempts = authId => {
  failedOTPAttempts.delete(authId);
};

const getResendAttemptCount = authId => resendOTPAttempts.get(authId) || 0;

const incrementResendAttempts = authId => {
  const nextCount = getResendAttemptCount(authId) + 1;
  resendOTPAttempts.set(authId, nextCount);
  return nextCount;
};

const clearResendAttempts = authId => {
  resendOTPAttempts.delete(authId);
};

const buildErrorResponse = (status, errorDescription) => ({
  ok: false,
  status,
  data: {
    status: 'ERROR',
    uniqueIdentifier: '',
    errorDescription,
  },
});

const buildAuthorizeQuery = params => {
  const qs = new URLSearchParams(params || {});
  return qs.toString();
};

const buildAuthorizeNoSessionLocation = params => {
  const authorizeQuery = buildAuthorizeQuery(params);
  return `https://secdev-txtsso.ad.portal.texas.gov:443/sso/UI/Login?realm=/TxTSSO&goto=https://dev.accounts-txt.ad.portal.texas.gov:443/sso/oauth2/authorize?${encodeURIComponent(authorizeQuery)}`;
};

const buildAuthorizeSessionLocation = params => {
  const redirectUri = params?.redirect_uri || 'https://example-app/callback';
  const query = new URLSearchParams({
    code: 'mock-auth-code',
    iss: 'https://secdev-txtsso.ad.portal.texas.gov:443/sso/oauth2',
    state: params?.state || 'mock-state',
    client_id: params?.client_id || 'mock-client',
  });

  return `${redirectUri}?${query.toString()}`;
};

const buildMockAuthorizeValue = params => {
  const authPayload = {
    scope: params?.scope || 'openid profile email',
    response_type: params?.response_type || 'code',
    redirect_uri: params?.redirect_uri || 'https://example-app/callback',
    state: params?.state || 'mock-state',
    client_id: params?.client_id || 'mock-client',
    code_challenge: params?.code_challenge || 'mock-code-challenge',
    code_challenge_method: params?.code_challenge_method || 'S256',
  };

  return btoa(JSON.stringify(authPayload));
};

const mockHandlers = {
  'GET /authorize': ({ params }) => {
    // TEST SCENARIO: mock_error=authorize_fail → Authorize endpoint fails with 500
    if (params?.mock_error === 'authorize_fail') {
      return {
        ok: false,
        status: 500,
      };
    }

    const isExistingSession = params?.mock_session === '1' || params?.has_session === 'true';
    const shouldReturnCode = isExistingSession || params?.userAliasId;

    if (shouldReturnCode) {
      // Return location WITH code
      const location = buildAuthorizeSessionLocation(params);
      const data = {
        location,
        userAliasId: 'mock-user-alias-12345',
        agencyId: "DPS"
      };
      if (isExistingSession) {
        data.userEmail = "john.doe@texas.gov";
        data.firstName = "John";
        data.lastName = "Doe";
        data.phoneNumber = "512-555-1234";
        data.address = "123 Main St, Austin, TX 78701";
      }

      return {
        ok: true,
        status: 200,
        data,
      };
    }

    // No session, no auth → return location WITHOUT code (redirect to login)
    const location = buildAuthorizeNoSessionLocation(params);
    return {
      ok: true,
      status: 200,
      data: {
        location,
        agencyId: "DPS"
      },
    };
  },

  'POST /authenticate': ({ body, headers }) => {
    const username = headers?.['X-OpenAM-Username'] || 'jane.doe@gmail.com';
    const password = headers?.['X-OpenAM-Password'];
    const deviceFingerprint = body?.input?.find(input => input?.name === 'IDToken0')?.value;
    const clientId = body?.client_id || '';
    const agencyId = body?.agencyId || '';

    if (username === 'locked@test.com') {
      return buildErrorResponse(423, 'user is locked since otp was incorrect');
    }

    if (username === 'unauthorized@test.com' || password === 'wrongpassword') {
      return buildErrorResponse(401, 'unauthorized');
    }

    if (username === 'wrongcreds@test.com') {
      return buildErrorResponse(401, 'user credentials are incorrect');
    }

    if (username === 'servererror@test.com') {
      return {
        ok: false,
        status: 500,
      };
    }

    if (getFailedAttemptCount(username) >= 5) {
      return buildErrorResponse(423, 'User is locked');
    }

    if (password === 'password' && deviceFingerprint?.includes('trusted-device')) {
      clearFailedAttempts(username);
      return {
        ok: true,
        status: 200,
        data: {
          tokenId: 'mock-token-id',
          successUrl: '/sso/console',
          realm: '/TxTSSO',
          userAliasId: 'mock-user-alias-12345',
        },
      };
    }

    if (password === 'password') {
      clearFailedAttempts(username);
      return {
        ok: true,
        status: 200,
        data: {
          authId: 'mock-auth-id',
          userAliasId: 'mock-user-alias-12345',
          callbacks: [
            {
              type: 'TextOutputCallback',
              output: [
                { name: 'message', value: 'client-script-placeholder' },
                { name: 'messageType', value: '4' },
              ],
            },
            {
              type: 'ChoiceCallback',
              output: [
                { name: 'prompt', value: 'Choose a contact' },
                { name: 'choices', value: ['** *** 1234', username] },
                { name: 'defaultChoice', value: 0 },
              ],
              input: [{ name: 'IDToken2', value: 0 }],
            },
          ],
        },
      };
    }

    const attemptCount = incrementFailedAttempts(username);

    if (attemptCount > 5) {
      return buildErrorResponse(423, 'User is locked');
    }

    return buildErrorResponse(401, 'User credentials are incorrect');
  },

  'POST /mfaChoices': ({ body, headers }) => {
    const username = headers?.['X-OpenAM-Username'] || 'jane.doe@gmail.com';
    const choiceCallback = body?.callbacks?.find(cb => cb?.type === 'ChoiceCallback');
    const selectedIndex = choiceCallback?.input?.find(input => input?.name === 'IDToken2')?.value;

    // TEST SCENARIO: Check for special test authIds from authenticate
    const authId = body?.authId || '';

    // TEST SCENARIO: mfa-error-500 → Server error
    if (authId === 'mfa-error-500') {
      return {
        ok: false,
        status: 500,
        data: {
          errorCode: 'INTERNAL_ERROR',
          errorDescription: 'Internal server error during MFA selection',
          errorId: 'ERR-MFA-500',
        },
      };
    }

    // TEST SCENARIO: mfa-error-invalid → Invalid input
    if (authId === 'mfa-error-invalid' || (selectedIndex !== 0 && selectedIndex !== 1)) {
      return buildErrorResponse(400, 'invalid input');
    }

    mockOTPAuthSequence += 1;
    const nextAuthId = `mock-auth-id-otp-${mockOTPAuthSequence}`;
    clearOTPAttempts(nextAuthId);
    clearResendAttempts(nextAuthId);

    return {
      ok: true,
      status: 200,
      data: {
        authId: nextAuthId,
        userAliasId: 'mock-user-alias-12345',
        callbacks: [
          {
            type: 'PasswordCallback',
            output: [
              {
                name: 'prompt',
                value: 'One Time Passcode',
              },
            ],
            input: [
              {
                name: 'IDToken1',
                value: '',
              },
            ],
          },
          {
            type: 'ConfirmationCallback',
            output: [
              { name: 'prompt', value: '' },
              { name: 'messageType', value: 0 },
              { name: 'options', value: ['Submit', 'Resend'] },
              { name: 'optionType', value: -1 },
              { name: 'defaultOption', value: 0 },
            ],
            input: [
              { name: 'IDToken2', value: 0 },
            ],
          },
          {
            type: 'ChoiceCallback',
            output: [
              { name: 'prompt', value: 'Do you want to save?' },
              { name: 'choices', value: ['No', 'Yes'] },
              { name: 'defaultChoice', value: 0 },
            ],
            input: [
              { name: 'IDToken0', value: 0 },
            ],
          },
          {
            type: 'TextOutputCallback',
            output: [
              { name: 'message', value: `OTP sent to option ${selectedIndex === 0 ? 'phone' : username}` },
              { name: 'messageType', value: '0' },
            ],
          },
        ],
      },
    };
  },

  'POST /processOTP': ({ body }) => {
    const callbacks = body?.callbacks || [];
    const authId = body?.authId;
    const otp = callbacks
      .flatMap(callback => callback?.input || [])
      .find(input => input?.name === 'IDToken1')?.value;
    const saveDevice = callbacks
      .flatMap(callback => callback?.input || [])
      .find(input => input?.name === 'IDToken0')?.value;
    const actionValue = callbacks
      .flatMap(callback => callback?.input || [])
      .find(input => input?.name === 'IDToken2')?.value;

    if (!authId) {
      return buildErrorResponse(400, 'Invalid input');
    }

    if (actionValue === 1) {
      const resendCount = incrementResendAttempts(authId);

      if (resendCount > 5) {
        return buildErrorResponse(400, 'Resend limit has been reached');
      }

      // TEST SCENARIO: Too many codes
      if (resendCount > 3) {
        return {
          ok: false,
          status: 400,
          data: {
            message: 'Too many resend requests',
            errorDescription: 'too many codes requested',
          },
        };
      }

      // Success - return same payload with updated authId
      return {
        ok: true,
        status: 200,
        data: {
          ...body,
          authId,
          userAliasId: 'mock-user-alias-12345',
        },
      };
    }

    // VERIFY OTP (actionValue === 0)
    
    // TEST SCENARIO: OTP = '111111' → Lockout (user is locked since otp was incorrect)
    if (otp === '111111') {
      return buildErrorResponse(423, 'user is locked since otp was incorrect');
    }

    // TEST SCENARIO: OTP = '222222' → Unauthorized
    if (otp === '222222') {
      return buildErrorResponse(401, 'unauthorized');
    }

    // TEST SCENARIO: OTP = '333333' → Invalid input
    if (otp === '333333') {
      return buildErrorResponse(400, 'invalid input');
    }

    // TEST SCENARIO: OTP = '444444' → Server error
    if (otp === '444444') {
      return {
        ok: false,
        status: 500,
        data: {
          errorCode: 'INTERNAL_ERROR',
          errorDescription: 'Internal server error during OTP verification',
          errorId: 'ERR-OTP-500',
        },
      };
    }

    // TEST SCENARIO: OTP = '555555' → Wrong OTP (returns callbacks, not error)
    if (otp === '555555') {
      return {
        ok: true,
        status: 200,
        data: {
          authId,
          userAliasId: 'mock-user-alias-12345',
          callbacks: body.callbacks, // Return same callbacks → try again
        },
      };
    }

    // VALID OTPs: '000000' or '759400'
    if ((otp === '000000' || otp === '759400') && (saveDevice === 0 || saveDevice === 1)) {
      clearOTPAttempts(authId);
      clearResendAttempts(authId);
      return {
        ok: true,
        status: 200,
        data: {
          tokenId: 'mock-otp-token-id',
          authValue: buildMockAuthorizeValue(body),
          successUrl: '/sso/console',
          realm: '/TxTSSO',
          userAliasId: 'mock-user-alias-12345',
        },
      };
    }

    // Failed attempts tracking for non-test OTPs
    const failedAttempts = incrementOTPAttempts(authId);

    if (failedAttempts >= 5) {
      return buildErrorResponse(423, 'User is locked since OTP was incorrect');
    }

    // Wrong OTP - return callbacks to try again
    return {
      ok: true,
      status: 200,
      data: {
        authId,
        userAliasId: 'mock-user-alias-12345',
        callbacks: body.callbacks,
      },
    };
  },

  'POST /getCustomerAppInfo': ({ body }) => {
    const clientId = body?.clientId || body?.customerAppClientId || '';
    const uniqueIdentifier = body?.uniqueIdentifier || '';

    // TEST SCENARIO: mock_error=get_customer_app_info_fail → Server error
    if (clientId === 'mock_error=get_customer_app_info_fail') {
      return {
        ok: false,
        status: 500,
        data: {
          errorCode: 'INTERNAL_ERROR',
          errorDescription: 'Internal server error during customer app info lookup',
          errorId: 'ERR-CUSTOMER-APP-INFO-500',
        },
      };
    }

    // Default SAML success path
    return {
      ok: true,
      status: 200,
      data: {
        applicationId: 'TXTSSO',
        agencyId: 'DPS',
        customerAppId: 26,
        uniqueIdentifier,
        appName: 'FORCE DREAM',
        appUrl: 'https://force-dream.texas.gov',
        utmSource: 'demo',
        utmMedium: 'demo',
        utmCampaign: 'demo',
      },
    };
  },

  'POST /logout': ({ body }) => {
    const userAliasId = body?.userAliasId || '';

    // TEST SCENARIO: userAliasId='session-timeout-error' → Session timeout (simple error body)
    if (userAliasId === 'session-timeout-error') {
      return {
        ok: false,
        status: 500,
        data: {
          error: 'Session validation failed',
        },
      };
    }

    // TEST SCENARIO: userAliasId='session-timeout-fault' → Session timeout (fault body)
    if (userAliasId === 'session-timeout-fault') {
      return {
        ok: false,
        status: 500,
        data: {
          fault: {
            faultstring: 'Execution of ServiceCallout SC-Logout failed. reason: ResponseCode 401 is not a success code for ExternalCallout',
            detail: { errorcode: 'steps.servicecallout.ExecutionFailed' },
          },
        },
      };
    }

    // Default: logout success
    return {
      ok: true,
      status: 200,
      data: {},
    };
  },

  'POST /verifyConsent': ({ body }) => {
    const customerAppClientId = body?.customerAppClientId || '';
    const userAliasId = body?.userAliasId || '';
    // Default returns 404 to show Grant Permission page in both flows
    // Use 'client-skip-consent' to test skipping grant permission (returns 200)

    // TEST SCENARIO: client-session-timeout → Session timeout during consent verification
    if (customerAppClientId === 'client-session-timeout') {
      return {
        ok: false,
        status: 500,
        data: {
          error: 'Session validation failed',
        },
      };
    }

    // TEST SCENARIO: client-error-500 → Server error
    if (customerAppClientId === 'client-error-500') {
      return {
        ok: false,
        status: 500,
        data: {
          errorCode: 'INTERNAL_ERROR',
          errorDescription: 'Internal server error during consent verification',
          errorId: 'ERR-CONSENT-500',
        },
      };
    }

    // TEST SCENARIO: client-skip-consent → Consent already exists (200)
    if (customerAppClientId === 'client-skip-consent') {
      return {
        ok: true,
        status: 200,
        data: {
          applicationId: 'TXTSSO',
          agencyId: 'DPS',
          uniqueIdentifier: 'test-uid-001',
          userAliasId: userAliasId || 'mock-user-alias-12345',
          customerAppClientId,
          accept: 'Y',
        },
      };
    }

    // Default: Consent not found - show grant-permission screen (404)
    return {
      ok: false,
      status: 404,
      data: {
        applicationId: 'TXTSSO',
        agencyId: 'DPS',
        uniqueIdentifier: 'test-uid-001',
        userAliasId: userAliasId || 'mock-user-alias-12345',
        customerAppClientId: customerAppClientId || 'force-dream',
      },
    };
  },

  'POST /acceptConsent': ({ body }) => {
    const userAliasId = body?.userAliasId || '';

    // TEST SCENARIO: userAliasId='session-timeout-accept' → Session timeout during consent acceptance
    if (userAliasId === 'session-timeout-accept') {
      return {
        ok: false,
        status: 500,
        data: {
          error: 'Session validation failed',
        },
      };
    }

    // TEST SCENARIO: error-accept → Server error during accept
    if (userAliasId === 'error-accept-500') {
      return {
        ok: false,
        status: 500,
        data: {
          errorCode: 'INTERNAL_ERROR',
          errorDescription: 'Internal server error during consent acceptance',
          errorId: 'ERR-ACCEPT-500',
        },
      };
    }

    // Success by default
    if (body?.applicationId && body?.agencyId && body?.consentType) {
      return {
        ok: true,
        status: 200,
        data: {
          applicationId: body.applicationId,
          agencyId: body.agencyId,
          uniqueIdentifier: body.uniqueIdentifier,
          userAliasId: body.userAliasId,
          customerAppClientId: body.customerAppClientId,
          accept: 'Y',
          consentType: body.consentType,
        },
      };
    }

    // Validation error - missing required fields
    return {
      ok: false,
      status: 400,
      data: {
        applicationId: body?.applicationId || '',
        agencyId: body?.agencyId || '',
        uniqueIdentifier: body?.uniqueIdentifier,
        errorCode: 'TXTSSOCONSERR0001',
        message: 'Request validation failed',
        fieldErrors: [
          !body?.applicationId && 'applicationId: applicationId is required',
          !body?.agencyId && 'agencyId: agencyId is required',
          !body?.consentType && 'consentType: consentType is required',
        ].filter(Boolean),
      },
    };
  },
};

export default mockHandlers;
